import React from "react";

import DescriptiveTooltip from "../../components/DescriptiveTooltip";
import type { ResourceType } from "../../store/api/resourceTypes";
import defaultFieldRenderer from "../defaultFieldRenderer";
import {
  RESOURCE_TYPE_ICONS,
  RESOURCE_TYPE_LABELS,
} from "../resourceTypeIcons";
import type { FieldRenderer, ResourceFieldValue } from "../types";

type ResourceRendererComponentProps = {
  resourceType: ResourceType | undefined;
  defaultNode: React.ReactElement;
};

const ResourceRendererComponent = React.memo(
  function ResourceRendererComponent({
    resourceType,
    defaultNode,
  }: ResourceRendererComponentProps) {
    const Icon = resourceType ? RESOURCE_TYPE_ICONS[resourceType] : null;
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        {Icon && resourceType && (
          <DescriptiveTooltip title={RESOURCE_TYPE_LABELS[resourceType]}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
                marginRight: 6,
              }}
            >
              <Icon fontSize="small" color="action" />
            </span>
          </DescriptiveTooltip>
        )}
        <div style={{ minWidth: 0 }}>{defaultNode}</div>
      </div>
    );
  },
);

const renderer: FieldRenderer = (fieldValue: unknown) => {
  const resource = fieldValue as ResourceFieldValue;
  const resourceName = resource?.name ?? "";
  const resourceType = resource?.resourceType;
  const hasCascades = resource?.cascades ?? false;

  const displayName = hasCascades ? `${resourceName}.*` : resourceName;
  const { node: defaultNode } = defaultFieldRenderer(displayName);

  return {
    formattedText: displayName,
    node: (
      <ResourceRendererComponent
        resourceType={resourceType}
        defaultNode={defaultNode}
      />
    ),
  };
};

export default renderer;
